/**
 * Wishlist engine for Prestige theme
 * Stores wishlist items in localStorage, scoped per-customer if logged in.
 * Dispatches `wishlist:updated` on `window` whenever the wishlist changes.
 */

const WishlistStore = (() => {
  const GUEST_KEY = 'wishlist:guest';

  function customerId() {
    return window.WishlistConfig && window.WishlistConfig.customerId
      ? String(window.WishlistConfig.customerId)
      : null;
  }

  function storageKey() {
    const id = customerId();
    return id ? `wishlist:customer:${id}` : GUEST_KEY;
  }

  function readRaw(key) {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      console.error('Wishlist: failed to read storage', e);
      return [];
    }
  }

  function writeRaw(key, items) {
    try {
      localStorage.setItem(key, JSON.stringify(items));
    } catch (e) {
      console.error('Wishlist: failed to write storage', e);
    }
  }

  function getAll() {
    return readRaw(storageKey());
  }

  function has(id) {
    return getAll().some((item) => String(item.id) === String(id));
  }

  function save(items) {
    writeRaw(storageKey(), items);
    window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items } }));
  }

  function add(item) {
    const items = getAll();
    if (items.some((i) => String(i.id) === String(item.id))) return;
    items.unshift(item);
    save(items);
  }

  function remove(id) {
    const items = getAll().filter((item) => String(item.id) !== String(id));
    save(items);
  }

  function toggle(item) {
    if (has(item.id)) {
      remove(item.id);
      return false;
    }
    add(item);
    return true;
  }

  // Called once on page load if a customer is logged in: merges any items
  // saved as a guest into that customer's own wishlist bucket, then clears
  // the guest bucket so it isn't merged again later.
  function mergeGuestIntoCustomer() {
    const id = customerId();
    if (!id) return;

    const guestItems = readRaw(GUEST_KEY);
    if (guestItems.length === 0) return;

    const customerItems = readRaw(`wishlist:customer:${id}`);
    const merged = [...customerItems];

    guestItems.forEach((guestItem) => {
      if (!merged.some((i) => String(i.id) === String(guestItem.id))) {
        merged.push(guestItem);
      }
    });

    writeRaw(`wishlist:customer:${id}`, merged);
    writeRaw(GUEST_KEY, []);
    window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items: merged } }));
  }

  return { getAll, has, add, remove, toggle, mergeGuestIntoCustomer };
})();

window.WishlistStore = WishlistStore;

document.addEventListener('DOMContentLoaded', () => {
  WishlistStore.mergeGuestIntoCustomer();
});


class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('.wishlist-button__toggle');
    this.productId = this.dataset.productId;

    this.item = {
      id: this.productId,
      title: this.dataset.productTitle,
      url: this.dataset.productUrl,
      price: this.dataset.productPrice,
      image: this.dataset.productImage,
    };

    this.updateState();

    this.button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      window.WishlistStore.toggle(this.item);
    });

    this._onUpdate = () => this.updateState();
    window.addEventListener('wishlist:updated', this._onUpdate);
  }

  disconnectedCallback() {
    window.removeEventListener('wishlist:updated', this._onUpdate);
  }

  updateState() {
    const active = window.WishlistStore.has(this.productId);
    this.classList.toggle('is-active', active);
    this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
  }
}

customElements.define('wishlist-button', WishlistButton);


function renderWishlistPage() {
  const itemsContainer = document.querySelector('[data-wishlist-page-items]');
  const emptyState = document.querySelector('[data-wishlist-page-empty]');

  if (!itemsContainer) return; // not on the wishlist page

  const items = window.WishlistStore.getAll();

  emptyState.hidden = items.length > 0;
  itemsContainer.innerHTML = '';

  items.forEach((item) => {
    const row = document.createElement('div');
    row.className = 'wishlist-page__item';
    row.innerHTML = `
      <a href="${item.url}" class="wishlist-page__item-image">
        <img src="${item.image}" alt="${item.title}" loading="lazy">
      </a>
      <div class="wishlist-page__item-info">
        <div class="wishlist-page__item-text">
          <a href="${item.url}" class="wishlist-page__item-title">${item.title}</a>
          <p class="wishlist-page__item-price text-subdued">${item.price}</p>
        </div>
        <div class="wishlist-page__item-actions">
          <button type="button" class="link-faded" data-remove-id="${item.id}">Remove</button>
          <a href="${item.url}" class="button button--sm">Shop Now</a>
        </div>
      </div>
    `;
    itemsContainer.appendChild(row);
  });

  itemsContainer.querySelectorAll('[data-remove-id]').forEach((btn) => {
    btn.addEventListener('click', () => {
      window.WishlistStore.remove(btn.dataset.removeId);
    });
  });
}

function updateWishlistHeaderCount() {
  const count = window.WishlistStore.getAll().length;
  document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
    const visible = count > 0;
    el.classList.toggle('is-visible', visible);
    el.hidden = !visible;
  });

  const titleCount = document.querySelector('[data-wishlist-title-count]');
  if (titleCount) {
    const itemText = count === 1 ? 'Item' : 'Items';
    titleCount.textContent = `(${count} ${itemText})`;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  renderWishlistPage();
  updateWishlistHeaderCount();
});

window.addEventListener('wishlist:updated', () => {
  renderWishlistPage();
  updateWishlistHeaderCount();
});